import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { printContent } from '../../lib/exportUtils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import {
  Loader2, Check, Calculator, ArrowRight, X,
  Save, Trash2, Edit3, Download, FolderOpen,
  Info, Printer, BarChart2, Landmark
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { fmt, API_URL, complexityConfig } from './constants';
import { IncomeProfileSection } from './IncomeProfile';

const Tip = ({ children, text, side = 'top' }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);

const CompareRow = ({ label, plans, accessor, highlight, best }) => {
  const bestVal = best ? Math.max(...plans.map(p => best(p) || 0)) : null;
  return (
    <div className={cn("grid gap-4 items-center py-2.5 border-b border-border", plans.length === 2 ? "grid-cols-[140px_1fr_1fr]" : "grid-cols-[140px_1fr_1fr_1fr]")}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {plans.map(p => {
        const val = accessor(p);
        const isBest = best && best(p) === bestVal && plans.filter(x => best(x) === bestVal).length === 1;
        return (
          <span key={p.plan_id} className={cn("text-xs font-mono tabular-nums text-center font-semibold", isBest && highlight === 'emerald' && "text-emerald-600 dark:text-emerald-400", isBest && highlight === 'violet' && "text-violet-600 dark:text-violet-400", !isBest && "text-muted-foreground")}>
            {val}
            {isBest && <span className="ml-1 text-[9px] font-normal text-emerald-500">best</span>}
          </span>
        );
      })}
    </div>
  );
};

function SimulatorTab({ workspaceId }) {
  const [simulation, setSimulation] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enabledStrategies, setEnabledStrategies] = useState({});
  const [liability, setLiability] = useState(null);
  const [liabilityLoading, setLiabilityLoading] = useState(false);

  const totalSavings = simulation?.simulation?.reduce((sum, s) => {
    if (s.applicable && enabledStrategies[s.strategy_id]) return sum + (s.estimated_savings || 0);
    return sum;
  }, 0) || 0;

  const fetchLiability = useCallback(async (savings = 0) => {
    if (!workspaceId) return;
    setLiabilityLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-simulator/liability?strategy_savings=${savings}`);
      if (res.ok) setLiability(await res.json());
    } catch (e) { console.error(e); }
    finally { setLiabilityLoading(false); }
  }, [workspaceId]);

  useEffect(() => {
    const fetchLatest = async () => {
      if (!workspaceId) return;
      try {
        const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-simulator/latest`);
        if (res.ok) {
          const d = await res.json();
          if (d.simulation?.length > 0) {
            setSimulation(d);
            const initial = {};
            let initSavings = 0;
            d.simulation.forEach(s => { if (s.applicable) { initial[s.strategy_id] = true; initSavings += (s.estimated_savings || 0); } });
            setEnabledStrategies(initial);
            fetchLiability(initSavings);
          } else {
            fetchLiability(0);
          }
        } else {
          fetchLiability(0);
        }
      } catch (e) { console.error(e); fetchLiability(0); }
      finally { setLoading(false); }
    };
    fetchLatest();
  }, [workspaceId, fetchLiability]);

  // Recalculate liability when strategy toggles change
  useEffect(() => {
    if (!simulation?.simulation) return;
    const newSavings = simulation.simulation.reduce((sum, s) => {
      if (s.applicable && enabledStrategies[s.strategy_id]) return sum + (s.estimated_savings || 0);
      return sum;
    }, 0);
    fetchLiability(newSavings);
  }, [enabledStrategies, simulation, fetchLiability]);

  const generate = async () => {
    if (!workspaceId) return;
    setGenerating(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-simulator/generate`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        setSimulation(d);
        const initial = {};
        let initSavings = 0;
        (d.simulation || []).forEach(s => { if (s.applicable) { initial[s.strategy_id] = true; initSavings += (s.estimated_savings || 0); } });
        setEnabledStrategies(initial);
      }
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const toggleStrategy = (id) => {
    setEnabledStrategies(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const enabledCount = Object.values(enabledStrategies).filter(Boolean).length;
  const [savePlanName, setSavePlanName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const savePlan = async () => {
    if (!savePlanName.trim() || !simulation) return;
    setSaving(true);
    try {
      const enabled = (simulation.simulation || []).filter(s => s.applicable && enabledStrategies[s.strategy_id]);
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-plans`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: savePlanName.trim(),
          liability: liability || {},
          strategies: enabled.map(s => ({ strategy_id: s.strategy_id, name: s.name, estimated_savings: s.estimated_savings, complexity: s.complexity, rationale: s.rationale })),
          total_savings: totalSavings,
          total_tax_reduction: liability?.strategy_tax_reduction || 0,
          enabled_strategy_ids: Object.keys(enabledStrategies).filter(k => enabledStrategies[k]),
        }),
      });
      if (res.ok) { setShowSaveDialog(false); setSavePlanName(''); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const buildPrintHtml = () => {
    const enabled = (simulation?.simulation || []).filter(s => s.applicable && enabledStrategies[s.strategy_id]);
    const L = liability || {};
    const marginal = (L.marginal_rate || 22) / 100;
    const stateRate = (L.state_tax_info?.effective_rate || 0) / 100;
    const combinedRate = marginal + stateRate;
    let totalTaxImpact = 0;
    const rows = enabled.map(s => {
      const savings = s.estimated_savings || 0;
      const taxImpact = Math.round(savings * combinedRate);
      totalTaxImpact += taxImpact;
      return `<tr><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-weight:600">${s.name}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;color:#059669;font-weight:700;text-align:right;font-family:monospace">$${savings.toLocaleString()}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;color:#7c3aed;font-weight:700;text-align:right;font-family:monospace">$${taxImpact.toLocaleString()}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;text-align:center"><span style="background:${s.complexity==='simple'?'#dcfce7':s.complexity==='moderate'?'#fef3c7':'#fce7f3'};color:${s.complexity==='simple'?'#166534':s.complexity==='moderate'?'#92400e':'#9d174d'};padding:2px 10px;border-radius:20px;font-size:11px;font-weight:600">${s.complexity}</span></td>
      <td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;color:#475569">${s.rationale}</td></tr>`;
    }).join('');
    const fsLabels = { single: 'Single', married_jointly: 'Married Filing Jointly', married_separately: 'Married Filing Separately', head_of_household: 'Head of Household' };
    const hasSt = L.state_tax > 0;
    const isRefund = L.refund_or_owed === 'refund';
    const f = (v) => '$' + (v||0).toLocaleString(undefined, {minimumFractionDigits:0, maximumFractionDigits:0});
    const liabHtml = L.taxable_income != null ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin:24px 0">
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:20px">
        <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.1em;color:#94a3b8;margin:0 0 12px">Income &amp; Deductions</h3>
        ${L.business_income > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0"><span style="color:#64748b;font-size:13px">Business Net Income</span><span style="font-family:monospace">${f(L.business_income)}</span></div>` : ''}
        ${L.other_income > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0"><span style="color:#64748b;font-size:13px">Other Income</span><span style="font-family:monospace">${f(L.other_income)}</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:8px 0;font-weight:700;border-top:1px solid #e2e8f0;margin-top:4px"><span>Gross Income</span><span style="font-family:monospace">${f(L.gross_income)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0 4px 16px"><span style="color:#64748b;font-size:12px">Standard Deduction</span><span style="font-family:monospace;color:#64748b">(${f(L.deductions?.standard_deduction)})</span></div>
        ${L.deductions?.se_deduction > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0 4px 16px"><span style="color:#64748b;font-size:12px">SE Tax Deduction</span><span style="font-family:monospace;color:#64748b">(${f(L.deductions.se_deduction)})</span></div>` : ''}
        ${L.deductions?.qbi_deduction > 0 ? `<div style="display:flex;justify-content:space-between;padding:4px 0 4px 16px"><span style="color:#64748b;font-size:12px">QBI Deduction</span><span style="font-family:monospace;color:#64748b">(${f(L.deductions.qbi_deduction)})</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:8px 0;font-weight:700;border-top:1px dashed #cbd5e1;margin-top:4px"><span>Taxable Income</span><span style="font-family:monospace">${f(L.taxable_income)}</span></div>
      </div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:20px">
        <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.1em;color:#94a3b8;margin:0 0 12px">Tax &amp; Bottom Line</h3>
        <div style="display:flex;justify-content:space-between;padding:6px 0"><span style="color:#64748b;font-size:13px">Federal Income Tax</span><span style="font-family:monospace">${f(L.federal_income_tax)}</span></div>
        ${L.self_employment_tax > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0"><span style="color:#64748b;font-size:13px">Self-Employment Tax</span><span style="font-family:monospace">${f(L.self_employment_tax)}</span></div>` : ''}
        ${hasSt ? `<div style="display:flex;justify-content:space-between;padding:6px 0"><span style="color:#64748b;font-size:13px">State Tax (${L.state_tax_info?.state_name||L.state})</span><span style="font-family:monospace">${f(L.state_tax)}</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:8px 0;font-weight:700;border-top:1px solid #e2e8f0;margin-top:4px"><span>Total Tax (Before)</span><span style="font-family:monospace">${f(L.total_tax_before_strategies)}</span></div>
        ${L.strategy_savings_applied > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;margin-top:8px"><span style="color:#059669;font-size:13px">Strategy Tax Reduction</span><span style="font-family:monospace;color:#059669;font-weight:600">(${f(L.strategy_tax_reduction)})</span></div>
        <div style="display:flex;justify-content:space-between;padding:8px 0;font-weight:700;border-top:1px solid #e2e8f0"><span>Total Tax (After)</span><span style="font-family:monospace">${f(L.total_tax_after_strategies)}</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:6px 0;margin-top:8px"><span style="color:#64748b;font-size:13px">Withholding Paid</span><span style="font-family:monospace;color:#64748b">(${f(L.withholding_paid)})</span></div>
        <div style="display:flex;justify-content:space-between;padding:14px 0 0;margin-top:8px;border-top:3px solid ${isRefund?'#059669':'#e11d48'}">
          <span style="font-size:16px;font-weight:800">${isRefund?'Estimated Refund':'Estimated Amount Owed'}</span>
          <span style="font-size:24px;font-weight:800;color:${isRefund?'#059669':'#e11d48'};font-family:monospace">${f(L.amount_owed)}</span>
        </div>
      </div></div>` : '';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Tax Savings Plan - CountBucks</title>
      <style>
        @media print { .no-print { display: none !important; } }
        body{font-family:system-ui,-apple-system,sans-serif;max-width:960px;margin:40px auto;padding:0 24px;color:#0f172a}
        .action-bar{display:flex;gap:10px;margin-bottom:24px;padding:12px 16px;background:#f1f5f9;border-radius:10px;align-items:center}
        .action-bar button{padding:8px 20px;border-radius:8px;font-weight:600;font-size:14px;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:6px}
        .btn-print{background:#0f172a;color:white}.btn-print:hover{background:#1e293b}
        .action-bar span{color:#64748b;font-size:12px;margin-left:auto}
        @media print { .no-print { display: none !important; } @page { margin: 0.5in; } }
      </style>
    </head><body>
      <div class="action-bar no-print">
        <button class="btn-print" onclick="window.print()">Print / Save as PDF</button>
        <span>Tip: In the print dialog, select "Save as PDF" as destination to download</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px">
        <div><h1 style="margin:0;font-size:28px">Tax Savings Plan</h1>
        <p style="color:#64748b;margin:4px 0 0">Generated ${new Date().toLocaleDateString()} by CFO Buck &middot; CountBucks</p></div>
        <div style="text-align:right;font-size:13px;color:#64748b">
          <p style="margin:0">${L.tax_year || new Date().getFullYear()} Tax Year</p>
          <p style="margin:2px 0 0">${fsLabels[L.filing_status] || 'Single'}${L.state_tax_info ? ' &middot; ' + L.state_tax_info.state_name : ''}</p>
          <p style="margin:2px 0 0">Effective: ${L.effective_rate || 0}% | Marginal: ${L.marginal_rate || 0}%</p>
        </div>
      </div>${liabHtml}
      <h2 style="font-size:18px;margin:32px 0 16px;padding-top:16px;border-top:2px solid #e2e8f0">Selected Tax Strategies (${enabledCount})</h2>
      <div style="display:flex;gap:16px;margin:0 0 20px">
        <div style="flex:1;background:#f0fdf4;padding:14px 20px;border-radius:12px;display:flex;justify-content:space-between;align-items:center">
          <span style="color:#166534;font-weight:600;font-size:13px">Total Est. Savings</span>
          <span style="font-size:24px;font-weight:800;color:#059669;font-family:monospace">$${totalSavings.toLocaleString()}</span>
        </div>
        <div style="flex:1;background:#f5f3ff;padding:14px 20px;border-radius:12px;display:flex;justify-content:space-between;align-items:center">
          <span style="color:#5b21b6;font-weight:600;font-size:13px">Tax Reduction</span>
          <span style="font-size:24px;font-weight:800;color:#7c3aed;font-family:monospace">${f(L.strategy_tax_reduction || totalTaxImpact)}</span>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse"><thead><tr style="background:#f1f5f9">
        <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#64748b">Strategy</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#64748b">Est. Savings</th>
        <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#7c3aed">Tax Impact</th>
        <th style="padding:10px 12px;text-align:center;font-size:11px;text-transform:uppercase;color:#64748b">Complexity</th>
        <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#64748b">Rationale</th>
      </tr></thead><tbody>${rows}</tbody>
      <tfoot><tr style="background:#f1f5f9"><td style="padding:10px 12px;font-weight:700">Total</td>
        <td style="padding:10px 12px;color:#059669;font-weight:800;text-align:right;font-family:monospace">$${totalSavings.toLocaleString()}</td>
        <td style="padding:10px 12px;color:#7c3aed;font-weight:800;text-align:right;font-family:monospace">$${totalTaxImpact.toLocaleString()}</td>
        <td colspan="2"></td></tr></tfoot></table>
      <p style="color:#94a3b8;font-size:11px;margin-top:24px">Disclaimer: Estimates only. Confirm with a CPA before implementation.</p>
    </body></html>`;
  };

  const handlePrint = () => {
    try {
      const html = buildPrintHtml();
      const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const styles = styleMatch ? styleMatch.join('\n') : '';
      const body = bodyMatch ? bodyMatch[1] : html;
      printContent(`${styles}${body}`, {
        filename: `CountBucks_Tax_Plan_${new Date().toISOString().slice(0,10)}.pdf`
      });
    } catch (err) {
      console.error('Print error:', err);
    }
  };

  const handleDownload = () => {
    handlePrint();
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>;

  const handleIncomeChange = () => {
    const savings = simulation?.simulation?.reduce((sum, s) => {
      if (s.applicable && enabledStrategies[s.strategy_id]) return sum + (s.estimated_savings || 0);
      return sum;
    }, 0) || 0;
    fetchLiability(savings);
  };

  return (
    <div className="space-y-6" data-testid="tax-simulator-tab">
      {/* Save Plan Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="save-plan-dialog">
          <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border p-6">
            <h3 className="text-base font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Save Tax Plan</h3>
            <p className="text-xs text-muted-foreground mb-4">Give this plan a descriptive name so you can find and compare it later.</p>
            <Input value={savePlanName} onChange={(e) => setSavePlanName(e.target.value)} placeholder="e.g., Aggressive 2026 Strategy"
              className="mb-4" autoFocus data-testid="save-plan-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') savePlan(); }} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowSaveDialog(false); setSavePlanName(''); }}>Cancel</Button>
              <Button size="sm" onClick={savePlan} disabled={saving || !savePlanName.trim()}
                className="bg-gradient-to-r from-violet-500 to-indigo-600 text-white" data-testid="confirm-save-plan-btn">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save Plan
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-violet-500/5 via-indigo-500/5 to-emerald-500/5 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Tax Savings Simulator</h2>
              <p className="text-xs text-muted-foreground">CFO Buck analyzes your financials and models hypothetical savings for each applicable strategy.</p>
            </div>
            <div className="flex items-center gap-2">
              {simulation?.simulation?.length > 0 && (
                <>
                  <Tip text="Save this tax plan with a name so you can compare it later.">
                    <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(true)} className="text-xs" data-testid="save-tax-plan">
                      <Save className="w-3.5 h-3.5 mr-1.5" />Save Plan
                    </Button>
                  </Tip>
                  <Tip text="Download a PDF of your tax plan.">
                    <Button variant="outline" size="sm" onClick={handlePrint} className="text-xs" data-testid="print-tax-plan">
                      <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Plan
                    </Button>
                  </Tip>
                  <Tip text="Save your tax plan as a PDF file.">
                    <Button variant="outline" size="sm" onClick={handleDownload} className="text-xs" data-testid="download-tax-plan">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Save PDF
                    </Button>
                  </Tip>
                </>
              )}
              <Tip text="CFO Buck will analyze your real financial data against all 30+ tax strategies and estimate specific dollar savings for each.">
                <Button size="sm" onClick={generate} disabled={generating} className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white text-xs" data-testid="generate-simulation-btn">
                  {generating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing...</> : <><Calculator className="w-3.5 h-3.5 mr-1.5" />{simulation ? 'Regenerate Plan' : 'Generate Tax Plan'}</>}
                </Button>
              </Tip>
            </div>
          </div>
        </div>
      </Card>

      {/* Full Income Profile */}
      <IncomeProfileSection workspaceId={workspaceId} businessIncome={liability?.business_income || 0} onIncomeChange={handleIncomeChange} />

      {/* Estimated Tax Liability Card */}
      {liabilityLoading && !liability && <Skeleton className="h-64 rounded-xl" />}
      {liability && !liability.error && (() => {
        const fsLabels = { single: 'Single', married_jointly: 'Married Filing Jointly', married_separately: 'Married Filing Separately', head_of_household: 'Head of Household' };
        const hasStrategies = liability.strategy_savings_applied > 0;
        const isRefund = liability.refund_or_owed === 'refund';
        const LRow = ({ label, value, bold, indent, sub, accent }) => (
          <div className={cn("flex items-center justify-between py-1.5", bold && "font-semibold", indent && "pl-4", sub && "border-t border-dashed border-border pt-2 mt-1")}>
            <span className={cn("text-xs", bold ? "text-foreground" : "text-muted-foreground")}>{label}</span>
            <span className={cn("text-xs font-mono tabular-nums", accent ? accent : bold ? "text-foreground" : "text-muted-foreground")}>{value}</span>
          </div>
        );
        return (
          <Card className="border-border overflow-hidden" data-testid="tax-liability-card">
            <CardContent className="p-0">
              <div className="px-5 py-4 border-b border-border bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-violet-500" />
                      <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>Estimated Tax Liability</h3>
                      {liabilityLoading && <Loader2 className="w-3.5 h-3.5 text-violet-400 animate-spin" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {liability.tax_year} &middot; {fsLabels[liability.filing_status] || liability.filing_status}
                      {liability.state_tax_info && <> &middot; {liability.state_tax_info.state_name}</>}
                      {' '}&middot; Effective Rate: {liability.effective_rate}% &middot; Marginal: {liability.marginal_rate}%
                    </p>
                  </div>
                  <Tip text="This calculation uses official IRS tax brackets and includes self-employment tax, QBI deduction, standard deduction, and approximate state income tax. It updates dynamically as you toggle strategies.">
                    <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                  </Tip>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                {/* Left: Income & Deductions */}
                <div className="px-5 py-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2">Income & Deductions</p>
                  {liability.business_income > 0 && <LRow label="Business Net Income" value={fmt(liability.business_income)} />}
                  {liability.other_income > 0 && <LRow label="Other Income (W-2, etc.)" value={fmt(liability.other_income)} />}
                  <LRow label="Gross Income" value={fmt(liability.gross_income)} bold />
                  <div className="my-2" />
                  <LRow label="Standard Deduction" value={`(${fmt(liability.deductions.standard_deduction)})`} indent />
                  {liability.deductions.se_deduction > 0 && <LRow label="SE Tax Deduction" value={`(${fmt(liability.deductions.se_deduction)})`} indent />}
                  {liability.deductions.qbi_deduction > 0 && <LRow label="QBI Deduction (Sec 199A)" value={`(${fmt(liability.deductions.qbi_deduction)})`} indent />}
                  <LRow label="Taxable Income" value={fmt(liability.taxable_income)} bold sub />
                </div>

                {/* Right: Tax & Bottom Line */}
                <div className="px-5 py-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2">Tax & Bottom Line</p>
                  <LRow label="Federal Income Tax" value={fmt(liability.federal_income_tax)} />
                  {liability.self_employment_tax > 0 && <LRow label="Self-Employment Tax" value={fmt(liability.self_employment_tax)} />}
                  {liability.state_tax > 0 && (
                    <Tip text={`${liability.state_tax_info?.state_name} state income tax at ~${liability.state_tax_info?.effective_rate}% effective rate. This is an approximate calculation.`}>
                      <div><LRow label={`State Tax (${liability.state_tax_info?.state_name || liability.state})`} value={fmt(liability.state_tax)} /></div>
                    </Tip>
                  )}
                  <LRow label="Total Tax (Before Strategies)" value={fmt(liability.total_tax_before_strategies)} bold />
                  {liability.ptet_info && liability.ptet_info.incremental_deduction > 0 && (
                    <Tip text={`PTET lets your entity deduct ${fmt(liability.ptet_info.ptet_deduction)} in state taxes at the entity level, bypassing the ${fmt(liability.ptet_info.salt_cap)} SALT cap. This creates an additional ${fmt(liability.ptet_info.incremental_deduction)} federal deduction.`}>
                      <div className="mt-1 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/15 border border-violet-200/50 dark:border-violet-800/50 cursor-help" data-testid="ptet-info">
                        <div className="flex items-center gap-1.5">
                          <Info className="w-3 h-3 text-violet-500 flex-shrink-0" />
                          <span className="text-[10px] font-medium text-violet-700 dark:text-violet-300">PTET Eligible &mdash; potential additional {fmt(liability.ptet_info.incremental_deduction)} federal deduction</span>
                        </div>
                      </div>
                    </Tip>
                  )}
                  {hasStrategies && (
                    <>
                      <div className="my-2" />
                      <LRow label="Strategy Tax Reduction" value={`(${fmt(liability.strategy_tax_reduction)})`} accent="text-emerald-600 dark:text-emerald-400" />
                      <LRow label="Total Tax (After Strategies)" value={fmt(liability.total_tax_after_strategies)} bold />
                    </>
                  )}
                  <div className="my-2" />
                  <LRow label="Withholding Already Paid" value={`(${fmt(liability.withholding_paid)})`} />
                  <div className={cn("flex items-center justify-between mt-3 pt-3 border-t-2", isRefund ? "border-emerald-300 dark:border-emerald-700" : "border-rose-300 dark:border-rose-700")}>
                    <span className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {isRefund ? 'Estimated Refund' : 'Estimated Amount Owed'}
                    </span>
                    <span className={cn("text-xl font-extrabold tabular-nums", isRefund ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400")} style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="tax-bottom-line">
                      {fmt(liability.amount_owed)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bracket breakdown - compact */}
              {liability.bracket_breakdown?.length > 0 && (
                <div className="px-5 py-3 border-t border-border bg-muted/30">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2">Tax Bracket Breakdown</p>
                  <div className="flex gap-1 h-5 rounded-md overflow-hidden" data-testid="bracket-bars">
                    {liability.bracket_breakdown.map((b, i) => {
                      const pct = liability.federal_income_tax > 0 ? (b.tax_in_bracket / liability.federal_income_tax) * 100 : 0;
                      const colors = ['bg-emerald-400', 'bg-blue-400', 'bg-violet-400', 'bg-amber-400', 'bg-rose-400', 'bg-pink-400', 'bg-red-500'];
                      return (
                        <Tip key={i} text={`${b.rate_pct} bracket: ${fmt(b.income_in_bracket)} taxed → ${fmt(b.tax_in_bracket)}`}>
                          <div className={cn("h-full rounded-sm cursor-help transition-all hover:opacity-80", colors[i] || colors[0])} style={{ width: `${Math.max(pct, 4)}%` }}>
                            <span className="text-[9px] font-bold text-white px-1 leading-5 truncate block">{b.rate_pct}</span>
                          </div>
                        </Tip>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}


      {!simulation?.simulation?.length && !generating ? (
        <Card className="border-dashed"><CardContent className="py-16 text-center">
          <Calculator className="w-10 h-10 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-base font-semibold text-foreground mb-2">No simulation yet</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">Click "Generate Tax Plan" to have CFO Buck analyze your financials and model potential tax savings across all available strategies.</p>
        </CardContent></Card>
      ) : simulation?.simulation?.length > 0 && (
        <>
          {/* Savings Summary */}
          <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10">
            <CardContent className="p-6 text-center">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">Total Estimated Tax Savings</p>
              <Tip text="This is the combined estimated savings if you implement all selected strategies below. Amounts are modeled by CFO Buck based on your actual financial data. Always confirm with your CPA.">
                <p className="text-4xl font-extrabold text-emerald-600 dark:text-emerald-400 cursor-help" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="total-savings">{fmt(totalSavings)}</p>
              </Tip>
              <p className="text-xs text-muted-foreground mt-1">{enabledCount} of {simulation.applicable_count} applicable strategies selected</p>
              <p className="text-[10px] text-muted-foreground mt-2">Toggle strategies on/off below to see how your savings change</p>
            </CardContent>
          </Card>

          {/* Strategy List */}
          <div className="space-y-3" data-testid="simulation-results">
            {simulation.simulation.filter(s => s.applicable).sort((a,b) => (a.priority||5) - (b.priority||5)).map((s) => {
              const cx = complexityConfig[s.complexity] || complexityConfig.moderate;
              const enabled = enabledStrategies[s.strategy_id];
              return (
                <Card key={s.strategy_id} className={cn("border transition-all", enabled ? "border-border" : "border-border opacity-60")} data-testid={`sim-strategy-${s.strategy_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Tip text={enabled ? 'Click to exclude this strategy from your savings total.' : 'Click to include this strategy in your savings total.'}>
                        <button onClick={() => toggleStrategy(s.strategy_id)} className={cn("w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all", enabled ? "bg-emerald-500 border-emerald-500" : "border-border dark:border-border")} data-testid={`sim-toggle-${s.strategy_id}`}>
                          {enabled && <Check className="w-3.5 h-3.5 text-white" />}
                        </button>
                      </Tip>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-foreground">{s.name}</h4>
                            <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 border-0 rounded-full", cx.color)}>{cx.label}</Badge>
                            {s.cpa_required && <Tip text="Consult a CPA before implementing this strategy."><span><Badge variant="outline" className="text-[9px] px-1.5 py-0 border-0 rounded-full bg-muted text-muted-foreground cursor-help">CPA</Badge></span></Tip>}
                            {s.already_used && <Tip text="You've already leveraged this strategy in a prior year. CFO Buck still included it in case conditions have changed."><span><Badge className="text-[9px] px-1.5 py-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 cursor-help"><Check className="w-2.5 h-2.5 mr-0.5" />Previously Used</Badge></span></Tip>}
                          </div>
                          <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(s.estimated_savings)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-2">{s.rationale}</p>
                        {s.next_step && (
                          <div className="flex items-center gap-1.5 text-[11px] text-violet-600 dark:text-violet-400 font-medium">
                            <ArrowRight className="w-3 h-3" /><span>Next: {s.next_step}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Not applicable */}
          {simulation.simulation.filter(s => !s.applicable).length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Not Applicable to Your Situation</p>
              <div className="space-y-2">
                {simulation.simulation.filter(s => !s.applicable).map(s => (
                  <div key={s.strategy_id} className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-muted text-muted-foreground">
                    <X className="w-4 h-4" /><span className="text-xs">{s.name}</span>
                    <span className="text-[11px] ml-auto">{s.rationale}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[10px] text-center text-muted-foreground pt-4">These estimates are based on your current financial data. Always confirm tax strategies with your CPA before implementation.</p>
        </>
      )}
    </div>
  );
}


// ─── SAVED PLANS TAB ────────────────────────────────────────────────────────
function SavedPlansTab({ workspaceId }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const fetchPlans = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-plans`);
      if (res.ok) setPlans(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const renamePlan = async (planId) => {
    if (!editName.trim()) return;
    try {
      await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-plans/${planId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      setEditingId(null);
      fetchPlans();
    } catch (e) { console.error(e); }
  };

  const deletePlan = async (planId) => {
    try {
      await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-plans/${planId}`, { method: 'DELETE' });
      setSelectedIds(prev => prev.filter(id => id !== planId));
      fetchPlans();
    } catch (e) { console.error(e); }
  };

  const toggleSelect = (planId) => {
    setSelectedIds(prev => {
      if (prev.includes(planId)) return prev.filter(id => id !== planId);
      if (prev.length >= 3) return prev;
      return [...prev, planId];
    });
  };

  const selectedPlans = plans.filter(p => selectedIds.includes(p.plan_id));

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="saved-plans-tab">
      {/* Header */}
      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-violet-500/5 via-indigo-500/5 to-emerald-500/5 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Saved Tax Plans</h2>
              <p className="text-xs text-muted-foreground">Review, compare, and manage your saved tax simulations. Select up to 3 plans to compare side-by-side.</p>
            </div>
            {selectedIds.length >= 2 && (
              <Button size="sm" onClick={() => setShowCompare(true)} className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white text-xs" data-testid="compare-plans-btn">
                <BarChart2 className="w-3.5 h-3.5 mr-1.5" />Compare ({selectedIds.length})
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Plans List */}
      {plans.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-16 text-center">
          <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-base font-semibold text-foreground mb-2">No saved plans yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Go to the Tax Simulator tab, generate a plan, and click "Save Plan" to save it here for future reference and comparison.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {plans.map(plan => {
            const isSelected = selectedIds.includes(plan.plan_id);
            const isEditing = editingId === plan.plan_id;
            const date = new Date(plan.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            const fsLabels = { single: 'Single', married_jointly: 'MFJ', married_separately: 'MFS', head_of_household: 'HoH' };
            return (
              <Card key={plan.plan_id} className={cn("border transition-all cursor-pointer", isSelected ? "border-violet-400 dark:border-violet-600 ring-1 ring-violet-200 dark:ring-violet-800" : "border-border hover:border-primary/30")} data-testid={`saved-plan-${plan.plan_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleSelect(plan.plan_id)}
                      className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all", isSelected ? "bg-violet-500 border-violet-500" : "border-border dark:border-border", !isSelected && selectedIds.length >= 3 && "opacity-30 cursor-not-allowed")}
                      disabled={!isSelected && selectedIds.length >= 3} data-testid={`select-plan-${plan.plan_id}`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        {isEditing ? (
                          <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm max-w-xs" autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') renamePlan(plan.plan_id); if (e.key === 'Escape') setEditingId(null); }}
                            onBlur={() => renamePlan(plan.plan_id)} data-testid="edit-plan-name-input" />
                        ) : (
                          <h4 className="text-sm font-semibold text-foreground truncate">{plan.name}</h4>
                        )}
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <Tip text="Rename this plan">
                            <button onClick={(e) => { e.stopPropagation(); setEditingId(plan.plan_id); setEditName(plan.name); }} className="p-1 rounded hover:bg-muted hover:bg-muted" data-testid={`rename-plan-${plan.plan_id}`}>
                              <Edit3 className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                          </Tip>
                          <Tip text="Delete this plan">
                            <button onClick={(e) => { e.stopPropagation(); deletePlan(plan.plan_id); }} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-plan-${plan.plan_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-red-500" />
                            </button>
                          </Tip>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                        <span>{date}</span>
                        <span>{plan.strategies?.length || 0} strategies</span>
                        {plan.liability?.filing_status && <span>{fsLabels[plan.liability.filing_status] || plan.liability.filing_status}</span>}
                        {plan.liability?.state_tax_info?.state_name && <span>{plan.liability.state_tax_info.state_name}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Savings</span>
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(plan.total_savings)}</span>
                        </div>
                        {plan.total_tax_reduction > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Tax Reduction</span>
                            <span className="text-sm font-bold text-violet-600 dark:text-violet-400 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(plan.total_tax_reduction)}</span>
                          </div>
                        )}
                        {plan.liability?.amount_owed != null && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                              {plan.liability.refund_or_owed === 'refund' ? 'Refund' : 'Owed'}
                            </span>
                            <span className={cn("text-sm font-bold tabular-nums", plan.liability.refund_or_owed === 'refund' ? "text-emerald-600" : "text-rose-600")} style={{ fontFamily: "'Outfit', sans-serif" }}>
                              {fmt(plan.liability.amount_owed)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Compare Modal */}
      {showCompare && selectedPlans.length >= 2 && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" data-testid="compare-modal">
          <div className="w-full max-w-5xl bg-card rounded-2xl shadow-2xl border border-border my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>Compare Tax Plans</h3>
                <p className="text-xs text-muted-foreground">{selectedPlans.length} plans selected</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowCompare(false)} data-testid="close-compare-modal"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-6 overflow-x-auto">
              {/* Plan headers */}
              <div className={cn("grid gap-4 mb-6", selectedPlans.length === 2 ? "grid-cols-2" : "grid-cols-3")}>
                {selectedPlans.map(p => (
                  <div key={p.plan_id} className="text-center p-4 rounded-xl bg-muted border border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-1 truncate">{p.name}</h4>
                    <p className="text-[11px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>

              {/* Comparison rows */}
              <div className="space-y-3">
                <CompareRow label="Total Savings" plans={selectedPlans} accessor={p => fmt(p.total_savings)} highlight="emerald" best={p => p.total_savings} />
                <CompareRow label="Tax Reduction" plans={selectedPlans} accessor={p => fmt(p.total_tax_reduction)} highlight="violet" best={p => p.total_tax_reduction} />
                <CompareRow label="Strategies" plans={selectedPlans} accessor={p => `${p.strategies?.length || 0}`} />
                <CompareRow label="Gross Income" plans={selectedPlans} accessor={p => fmt(p.liability?.gross_income)} />
                <CompareRow label="Taxable Income" plans={selectedPlans} accessor={p => fmt(p.liability?.taxable_income)} />
                <CompareRow label="Federal Tax" plans={selectedPlans} accessor={p => fmt(p.liability?.federal_income_tax)} />
                {selectedPlans.some(p => p.liability?.self_employment_tax > 0) && (
                  <CompareRow label="SE Tax" plans={selectedPlans} accessor={p => fmt(p.liability?.self_employment_tax)} />
                )}
                {selectedPlans.some(p => p.liability?.state_tax > 0) && (
                  <CompareRow label="State Tax" plans={selectedPlans} accessor={p => p.liability?.state_tax > 0 ? `${fmt(p.liability.state_tax)} (${p.liability?.state_tax_info?.state_name || ''})` : '--'} />
                )}
                <CompareRow label="Total Tax (Before)" plans={selectedPlans} accessor={p => fmt(p.liability?.total_tax_before_strategies)} />
                <CompareRow label="Total Tax (After)" plans={selectedPlans} accessor={p => fmt(p.liability?.total_tax_after_strategies)} highlight="emerald" best={p => -(p.liability?.total_tax_after_strategies || 0)} />
                <CompareRow label="Withholding" plans={selectedPlans} accessor={p => fmt(p.liability?.withholding_paid)} />
                <CompareRow label="Refund / Owed" plans={selectedPlans} accessor={p => {
                  const isRefund = p.liability?.refund_or_owed === 'refund';
                  return `${isRefund ? 'Refund' : 'Owed'}: ${fmt(p.liability?.amount_owed)}`;
                }} />
              </div>

              {/* Strategy comparison */}
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-8 mb-3">Strategy Breakdown</h4>
              <div className="space-y-2">
                {(() => {
                  const allIds = [...new Set(selectedPlans.flatMap(p => (p.strategies || []).map(s => s.strategy_id)))];
                  return allIds.map(sid => {
                    const stratName = selectedPlans.flatMap(p => p.strategies || []).find(s => s.strategy_id === sid)?.name || sid;
                    return (
                      <div key={sid} className={cn("grid gap-4 items-center py-2 border-b border-border", selectedPlans.length === 2 ? "grid-cols-[1fr_1fr_1fr]" : "grid-cols-[1fr_1fr_1fr_1fr]")}>
                        <span className="text-xs font-medium text-muted-foreground truncate">{stratName}</span>
                        {selectedPlans.map(p => {
                          const s = (p.strategies || []).find(x => x.strategy_id === sid);
                          return <span key={p.plan_id} className={cn("text-xs font-mono tabular-nums text-center", s ? "text-emerald-600 dark:text-emerald-400 font-semibold" : "text-muted-foreground/50")}>{s ? fmt(s.estimated_savings) : '--'}</span>;
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { SimulatorTab, SavedPlansTab };
